import styled from '@emotion/styled'
import { Box, CssBaseline, Paper } from '@mui/material'
import React from 'react'

import DaiLogo from '@/components/DaiLogo'
import GlobalStyle from '@/components/GlobalStyle'

const Root = styled.div`
  max-width: 800px;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  display: flex;
  min-height: 100%;
  flex-direction: column;
`

const Layout = ({ children }: { children: React.ReactNode }) => (
  <Root>
    <CssBaseline />
    <GlobalStyle />
    <DaiLogo />
    <Box sx={{ px: 6, width: '100%' }}>
      <Paper sx={{ padding: 6, width: '100%' }}>{children}</Paper>
    </Box>
  </Root>
)

const PrivacyPolicy: React.FC = () => {
  return (
    <Layout>
      <div>
        <h1>Delicious AI, LLC</h1>
        <h2>Privacy Policy</h2>
      </div>

      <p>
        <strong>Effective Date:</strong> September 25, 2023
      </p>

      <p>
        Thank you for doing business with Delicious AI, LLC ("
        <strong>Delicious AI</strong>," "<strong>Company</strong>," "
        <strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>").
        We welcome you and hope you find our websites, web applications, mobile
        application, and our other services and tools (collectively, the "
        <strong>Services</strong>") helpful and useful. We have adopted this
        privacy policy ("<strong>Privacy Policy</strong>") to help our website
        visitors, contractors, employees, current and potential customers, end
        users, and other business partners ("<strong>you</strong>" or "
        <strong>your</strong>") understand what Data we process, how and why we
        do so, and what your rights are regarding that Data.
      </p>

      <p>
        Any capitalized terms not otherwise defined herein have the meanings
        given to them in our Terms and Conditions.
      </p>

      <p>
        We always seek to improve our Services to you, and that requires that we
        process information about you and your usage preferences. As we do so,
        we are absolutely committed to protecting your privacy and the security
        of your Personal Information.
      </p>

      <p>
        In this Privacy Policy, we use the word "<strong>Data</strong>" to
        describe all the information we process that relates to you and your use
        of our Services. "Data" is broken into different categories, which are
        defined in the "<strong>Data We Process and How We Use It</strong>"
        section of this Privacy Policy. We may refer to the different categories
        separately, but when we use the word "Data," we mean all the different
        categories described in this Privacy Policy. Because of the nature of
        our Services, the term "Data" also includes the information, including
        Personal Information, you gather, upload, or is otherwise processed when
        you use the Services. We do not process Personal Information without
        your consent. We rely on your compliance with our Terms and Conditions
        or other agreement(s) you may have with us to process your Data.
      </p>

      <p>
        The term "Data" does not apply to information which does not relate to
        an identified or identifiable individual or to personal information or
        data rendered anonymous in such a manner that the individual is not or
        no longer identifiable ("<strong>Anonymized Data</strong>"). We hold all
        rights in and may use Anonymized Data for our own purposes in any legal
        manner and without attribution or compensation to any person.
      </p>

      <p>
        This Privacy Policy applies to everybody who interacts with us online or
        otherwise. Because different portions of the Privacy Policy will apply
        differently to the various groups who interact with us, we have tried to
        clearly categorize the types of Data we process and how we do so. If you
        have any questions about this Privacy Policy or how we handle your Data,
        please email us at privacy@deliciousai.com.
      </p>

      <h2>DESCRIPTION OF SERVICES</h2>
      <p>
        We provide access to our unique mobile application and AI services that
        allows organizations to capture and analyze CPG products on display in
        retail. In this Privacy Policy, all tools and services made available in
        connection with our operations, including our website, web applications,
        mobile application, tools, and any other services that we provide
        directly to you, whether now known or developed later, are included in
        the term "<strong>Services</strong>."
      </p>

      <h2>LAWFUL BASIS FOR PROCESSING</h2>
      <p>
        Many jurisdictions require that we disclose to you the lawful basis for
        our processing of your Data. We do that in the "
        <strong>Data We Process and How We Use It</strong>" section and
        otherwise throughout this Privacy Policy. In general, our lawful basis
        for processing your Data is based on your specific consent or your
        contract with us.
      </p>

      <p>
        By accessing or using any of the Services or by otherwise interacting
        with us online, you consent to our processing of your Data as described
        in this Privacy Policy. If our processing of your Data is based on your
        consent, you may withdraw your consent at any time, and we will cease
        processing your Data. However, in some cases, this may result in your
        inability to receive partial or full access to the Services, and your
        withdrawal of consent does not limit our ability to use Anonymized Data
        for use by us in connection with our legitimate business efforts in the
        future. In addition, your withdrawal of consent may not prevent us from
        processing Data if we have processed such Data pursuant to a different
        lawful basis or to preserve legal claims. For example, if you give your
        consent for us to process your Data, but we are also required by law to
        process your Data, that separate "lawful basis" will still apply, even
        if you withdraw your consent.
      </p>

      <p>
        When you enter into an agreement with us, either by registering for and
        accessing the Services, by executing an agreement in hard copy, or by
        clicking "I Accept" or similar language online, we will process your
        Data for the purposes of fulfilling the terms of our contract with you.
        In that case, our processing of your Data is based on the contract, so
        your withdrawal of consent will only be effective after the purposes for
        processing that Data have been fulfilled and after we no longer have a
        legal obligation to process that Data.
      </p>

      <p>
        In all cases, we will comply with applicable law and we will cease
        processing your Data after the legal right, obligation, or other lawful
        basis expires.
      </p>

      <h2>DATA WE PROCESS AND HOW WE USE IT</h2>
      <p>
        Listed below are categories of Data we process when you use our
        Services. We never sell or share (as defined for restrictive purposes in
        the CPRA) your Data, and we always have a lawful basis for processing
        the Data, but that lawful basis might be different for different
        categories, and we describe those uses below. Regardless, we never
        process the Data for any purpose other than the purpose for which we
        processed the Data in the first place, unless we get your prior explicit
        consent.
      </p>

      <h3>A. Registration Data</h3>
      <ol>
        <li>
          <strong>Data Description</strong>: Registration Data may consist of
          the name, email address, employee id, profile image, other contact
          information, and other information you provide us using the Services,
          both when you register for the Services and thereafter. Registration
          Data also includes your username, and other applicable registration
          information, if any and where applicable.
        </li>
        <li>
          <strong>Lawful Basis for Processing</strong>: Our lawful basis for
          processing Registration Data is our contract with you and your
          consent. We can only provide certain Services to you if we have the
          Registration Data, so we need to process that Registration Data during
          the term of our contract. Even when the Registration Data is not
          critically necessary to the provision of the Services, we may still
          process that Registration Data to facilitate our contractual
          interactions with you.
        </li>
        <li>
          <strong>How We Use It and Who We Share It With</strong>: Registration
          Data is accessible generally only to us and to you. In limited
          circumstances, we may share your Registration Data with our service
          providers, which parties help us provide the Services and are under
          obligations to protect the confidentiality of your Registration Data.
          We use Registration Data to provide the Services to you. At times, we
          will share the Registration Data with other third parties at your
          request.
        </li>
      </ol>

      <h3>B. Engagement Data</h3>
      <ol>
        <li>
          <strong>Data Description</strong>: Engagement Data consists of all the
          information you input or record using the Services, except as
          otherwise stated in this Privacy Policy. It also includes all
          information that is proprietary to you regarding your use of the
          Services (other than the data that qualifies as "Usage Data" below)
          that is processed by the Services. Engagement Data specifically
          includes the content of any text, personal preferences, and other
          information you input and include in the Services, such as feedback,
          comments, questions, video, audio, pictures, etc.
        </li>
        <li>
          <strong>Lawful Basis for Processing</strong>: Our lawful basis for
          processing Engagement Data is (1) our contract with you, (2) our
          obligation to provide you with the Services, and (3) our legitimate
          interest in improving our Services based on the Engagement Data we
          process.
        </li>
        <li>
          <strong>How We Use It and Who We Share It With</strong>: Your
          Engagement Data is accessible to us, to you, to our employees or
          designated agents (if applicable), and to limited third parties that
          we use to provide and improve the Services. If we share your
          Engagement Data with a third party, that party will be obligated to
          protect the confidentiality of your Engagement Data. Some Engagement
          Data, by its nature, will be shared with your employer, other users
          such as information in chat areas, forums, communities, etc. Your
          input of Engagement Data in these situations is voluntary and you
          assume all risk associated with uploading your Engagement Data. We
          rely on your compliance with our Terms and Conditions that you have
          all necessary rights to all Engagement Data you upload to the
          Services. Both during the term of our agreement with you and
          thereafter, we may also convert Engagement Data to Anonymized Data,
          and that Anonymized Data belongs solely to us to use in our sole
          discretion (including to sell or share Anonymized Data, which is not
          Data). To the extent we are required to delete any Engagement Data
          about you, we may still retain Anonymized Data that may have
          originated as your Engagement Data.
        </li>
      </ol>

      <h3>C. Usage Data</h3>
      <ol>
        <li>
          <strong>Data Description</strong>: Usage Data consists of the
          following and similar information:
          <ul>
            <li>
              Information about your interactions with the Services, most
              commonly our website or mobile application, which includes the
              date and time of any requests you make.
            </li>
            <li>
              Adjustments you make to the default state of the Services, such as
              custom categories or settings.
            </li>
            <li>
              The timing of the information you post to the Services including
              your interactions with us, but not including the content of those
              interactions, which would be included as Engagement Data.
            </li>
            <li>
              Technical data which may include URL information, cookie data,
              your IP address, the types of devices you are using to access or
              connect to the Services, unique device IDs, device attributes,
              network connection type (e.g. WiFi, 4G, LTE, Bluetooth) and
              provider, network and device performance, browser type, language,
              information enabling digital, mouse movements (including scrolling
              data). Please note, however, that if you disable certain
              functions, your use of the Services may be limited.
            </li>
            <li>
              Rights management, operating system, and application version.
            </li>
            <li>
              Motion-generated or orientation-generated mobile sensor data (e.g.
              accelerometer or gyroscope), if any, required for the purposes of
              providing specific features of the Services to you.
            </li>
          </ul>
        </li>
        <li>
          <strong>Lawful Basis for Processing</strong>: Our lawful basis for
          processing Usage Data is (1) our contract with you, (2) our obligation
          to provide you with the Services, and (3) our legitimate interest in
          improving our Services based on the Usage Data we process.
        </li>
        <li>
          <strong>How We Use It and Who We Share It With</strong>: Usage Data is
          accessible to us, to you, to your employer (where applicable), and to
          limited third parties that we use to provide and improve the Services.
          If we share your Usage Data with a third Party, that party will be
          obligated to protect the confidentiality of your Usage Data. We may
          also share Usage Data with other third parties at your specific
          request. We may use Usage Data to make improvements to the Services.
          Both during the term of our agreement with you and thereafter, we may
          also convert Usage Data to Anonymized Data, and that Anonymized Data
          belongs solely to us to use in our sole discretion (including to sell
          Anonymized Data, which is not Data). To the extent we are required to
          delete any Usage Data about you, we may still retain Anonymized Data
          that may have originated as your Usage Data.
        </li>
      </ol>

      <h3>D. Supplemental Mobile Data</h3>
      <ol>
        <li>
          <strong>Data Description</strong>: Where applicable to the Services,
          Supplemental Mobile Data consists of the following and similar
          information:
          <ul>
            <li>
              <strong>Your precise mobile device location</strong> - If you give
              us permission to access your precise location, this enables us to
              access your GPS or Bluetooth to provide location-aware
              functionality in the Services. Please note that this does not
              include your IP address. We use your IP address to determine
              non-precise location, for example, what country you are in to
              comply with our licensing and other agreements; and
            </li>
            <li>
              <strong>Your photos/videos</strong> - If you give us permission to
              access your photos, videos, or camera, we will only access images
              and videos that you specifically choose to share with us and
              metadata related to those images, such as the type of file and the
              size of the image. We will never scan or import your photo library
              or camera roll.
            </li>
          </ul>
        </li>
        <li>
          <strong>Lawful Basis for Processing</strong>: Our lawful basis for
          processing Supplemental Mobile Data is (1) our contract with you, (2)
          our obligation to provide you with the Services, and (3) our
          legitimate interest in improving our Services based on the
          Supplemental Mobile Data we process.
        </li>
        <li>
          <strong>How We Use It and Who We Share It With</strong>: We only use
          Supplemental Mobile Data when you specifically authorize it, and we
          only share it with those parties who are strictly necessary for the
          purpose you have authorized.
        </li>
      </ol>

      <h2>SHARING YOUR INFORMATION</h2>
      <p>
        We do not sell or share (as defined for restrictive purposes in the
        CPRA) your Data with third parties. Except where a specific limitation
        is noted above, we may share (other than as defined for restrictive
        purposes in the CPRA) your Data as follows:
      </p>
      <ol>
        <li>
          <strong>At Your Instruction</strong>. If you request us to make your
          Data available to a third party, and such request furthers the
          purposes of our Services, we will do so.
        </li>
        <li>
          <strong>Sharing with Vendors</strong>. In certain cases, we use the
          services of third-party vendors, to assist us in providing the
          Services. We may share your Data with such vendors solely for that
          purpose, and we will require those parties to abide by our privacy
          policies or privacy policies substantially in consonance with ours.
        </li>
        <li>
          <strong>Service Providers</strong>. We may use a third party to
          provide specific Services on our behalf, including sending emails or
          messages to you, conducting surveys, processing transactions, or
          performing statistical analysis of our Services. In these cases, we
          may provide certain personal information, such as your name and email
          address and other information necessary for the service to be
          provided. However, these third parties are required to maintain the
          confidentiality of this information and are prohibited from retaining,
          sharing, storing, or using this information for any other purposes.
        </li>
        <li>
          <strong>Employer</strong>. If you are an end user of our web or mobile
          applications, we share with and process your Data on behalf of your
          potential or current employer.
        </li>
        <li>
          <strong>Business Transitions</strong>. In the event that we go through
          a business transition, such as a merger, acquisition, liquidation, or
          sale of all or a portion of our assets, the information we have about
          you will, in most instances, be part of the assets transferred. We
          reserve the right to transfer that information in connection with such
          transactions without notice to you. We will not be required to obtain
          your consent for such a transfer.
        </li>
        <li>
          <strong>Legal Disclosure</strong>. We may disclose your Data if
          required to do so by law or in the good faith belief that such action
          is necessary to conform to applicable law, comply with a judicial
          proceeding, court order, or legal process served on us, protect and
          defend our rights or property, or investigate, prevent or take action
          regarding illegal activities, suspected fraud, situations involving
          potential threats to the physical safety of any person, or violations
          of our Terms and Conditions.
        </li>
      </ol>

      <p>
        <strong>
          If we ever plan to process any Data in the future for any other
          purposes not identified above and we do not have a separate lawful
          basis for that new purpose for processing, we will only do so after
          obtaining your specific consent.
        </strong>
      </p>

      <p>
        <strong>
          Further, we do not sell or share (as defined for restrictive purposes
          in the CPRA) your Data for any purpose.
        </strong>
      </p>

      <h2>TECHNOLOGIES WE USE</h2>
      <p>
        The technologies we use for automatic Data processing may include the
        following:
      </p>
      <ul>
        <li>
          <strong>Cookies (or browser cookies)</strong>. A cookie is a small
          file placed on the hard drive of your computer. You may refuse to
          accept browser cookies by activating the appropriate setting on your
          browser. However, if you select this setting you may be unable to
          access certain parts of our Services. Unless you have adjusted your
          browser setting so that it will refuse cookies, our system will issue
          cookies when you direct your browser to our Services.
        </li>
        <li>
          <strong>Web Beacons</strong>. Pages of the Services and our emails may
          contain small electronic files known as web beacons (also referred to
          as clear gifs. pixel tags and single-pixel gifs) that permit us, for
          example, to count users who have visited those pages or opened an
          email and for other related website statistics (for example, recording
          the popularity of certain website content and verifying system and
          server integrity).
        </li>
        <li>
          <strong>Geolocation</strong>. We may use geolocation (or other
          similar) technology when you use our Services to determine your
          current location. If you do not want us to use your location to
          provide you the Services, you can turn off your location services in
          your device's settings.
        </li>
        <li>
          <strong>Widgets</strong>. We may use technologies such as widgets or
          APIs to display parts of our Services on sites that are operated by
          our business partners.
        </li>
        <li>
          <strong>Other Technologies</strong>. We may also use device
          identifiers, local storage, html modifiers, and different types of
          caching to help us understand the devices and users who access the
          Services. Those methods include device identifiers that are either
          hardware-based or software-based, persistent, or non-persistent, and
          which may identify either a device or a software module within a
          device (such as a web browser).
        </li>
      </ul>

      <h3>"Do Not Track" Options</h3>
      <p>
        Your web browser(s) may offer a "Do Not Track" option, which allows the
        individual to signal to operators of websites and web applications and
        services (including behavioral advertising services) that he or she does
        not wish such operators to track certain of his or her online activities
        over time and across different websites. We do our best to support Do
        Not Track requests but cannot guarantee full support based on the
        variety of internet browsers and technologies which means that we may
        process information about your online activity both while you are using
        the Services and after your use of the Services.
      </p>

      <h2>YOUR RIGHTS REGARDING YOUR DATA</h2>
      <p>
        Under applicable data protection, privacy, and other laws, you may have
        certain rights related to your access and control of your Data. Such
        rights may include the following:
      </p>
      <ol>
        <li>
          The right to access, correct, update, or request deletion of your
          Data.
        </li>
        <li>
          The right to object to processing or restrict the processing of your
          Data (Please note that if you exercise this right, it may limit or
          eliminate our ability to provide you the Services).
        </li>
        <li>The right to request portability of your Data.</li>
        <li>
          The right to opt-out of marketing communications we send you. You can
          exercise this right by clicking the "Unsubscribe" or "Opt-Out" link
          found in these communications.
        </li>
        <li>
          The right to not be subject to a decision based solely on automated
          processing, including profiling, known as Automatic Decision Making.
        </li>
        <li>
          The right to submit a complaint to any applicable regulatory authority
          about our processing activities.
        </li>
        <li>
          The right to opt-out of us sharing (as defined in the CPRA) your Data,
          including for direct marketing purposes, subject to certain legal
          exceptions.
        </li>
        <li>
          The right to limit use, disclosure, and restrict sensitive personal
          information (as defined in the CPRA).
        </li>
      </ol>

      <p>
        We may use additional processes to verify your identity before we reveal
        or delete any of your Data, including two-factor or two-step
        authentication measures to ensure we can identify you.
      </p>

      <p>
        This list may not include all of your rights under applicable laws. If
        you believe you have additional rights, please contact us using the
        methods in this Privacy Policy.
      </p>

      <p>
        Further, although we currently do not process Data without consent, if
        we at any time in the future process Data without your express consent,
        you may opt-out or withdraw consent at any time.
      </p>

      <p>
        Please note that exercising any of the above rights may limit or
        eliminate our ability to provide you the Services. If so, we may
        terminate the Services due to such requests.
      </p>

      <p>
        We will try to comply with your request(s) as soon as reasonably
        practicable and at the very least as required under applicable law. Upon
        receipt of your written request, we will provide you with a copy of your
        information, although in certain limited circumstances we may not be
        able to make all relevant information available to you, such as where
        that information also pertains to another user. In such circumstances we
        will provide reasons for the denial to you upon request.
      </p>

      <p>
        Please also note that if you opt-out of receiving marketing-related
        emails from us, we may still send you messages for administrative or
        other purposes directly relating to your use of the Services, and you
        cannot opt-out from receiving those messages while continuing to use the
        Services.
      </p>

      <p>
        Further, you may opt-out or disable certain functions on your particular
        device, preventing us from processing Data. For example, you may disable
        geolocation or GPS functionality on your mobile device or disable push
        notifications. If you disable such features, your ability to use and
        access the Services may be limited.
      </p>

      <p>
        To exercise any of these rights, or if you have any questions about our
        processing of your Data, please contact us at privacy@deliciousai.com.
      </p>

      <h3>A. Privacy for California Residents</h3>
      <p>
        California adopted the California Consumer Privacy Act ("
        <strong>CCPA</strong>"), which took effect at the beginning of 2020 and
        has now adopted the California Privacy Rights Act ("
        <strong>CPRA</strong>"), which fully took effect January 1, 2023. We
        comply with the requirements of the CCPA and CPRA to the extent they
        apply to us.
      </p>

      <p>
        If you are a California resident, you may request to exercise your
        rights for any Data we have processed in the 12 months prior to your
        request. Such request covers any categories, sources, purposes, and, if
        applicable, third parties to whom we share the Data. Further, you can
        exercise any of your rights free of discrimination, for example, we
        cannot increase the price of the Services or decrease the quality of the
        Services because you exercise your rights.
      </p>

      <p>
        Due to the nature of our Services, we typically act as a "service
        provider" as defined under the CCPA and CPRA. If you believe that this
        role should be defined differently, please contact us at{' '}
        <a href="mailto:privacy@deliciousai.com">privacy@deliciousai.com</a>.
      </p>

      <p>
        For more information, please direct your questions to us at
        privacy@deliciousai.com.
      </p>

      <h3>B. Other Data Privacy and Protection Laws</h3>
      <p>
        We strive to comply with all data protection and privacy laws in
        applicable jurisdictions, to the extent such laws apply to us and our
        Services. We strive to be transparent about our data processing
        activities and have disclosed our practices throughout this Privacy
        Policy. If you have any questions about your rights under any applicable
        data protection and privacy laws, please contact us
        privacy@deliciousai.com.
      </p>

      <h2>SECURITY</h2>
      <p>
        The security of your Data is important to us. We use commercially
        reasonable efforts to process your Data in a secure environment. We take
        technical, contractual, administrative, and physical security steps
        designed to protect Data that you provide to us. We have implemented
        procedures designed to limit the dissemination of your Data to only such
        designated staff as are reasonably necessary to carry out the stated
        purposes we have communicated to you.
      </p>

      <h2>THIRD-PARTY POLICIES</h2>
      <p>
        You may be able to access third-party websites and other tools and
        services or products via a link on our website, mobile application, or
        via our other tools. The privacy policies of these third parties are not
        under our control and may differ from ours. The use of any Data that you
        may provide to any third parties will be governed by the privacy policy
        of such third party or by your independent agreement with such third
        parties, as the case may be. If you have any doubts about the privacy of
        the information you are providing to a third party, we recommend that
        you contact that third party directly for more information or to review
        its privacy policy.
      </p>

      <p>
        This Privacy Policy does not address, and we are not responsible for,
        the privacy, information, or other practices of any third parties,
        including any third party operating any offering, site, or other
        products and Services used in connection with the Services. The
        inclusion of a link does not imply endorsement of the linked site or
        service by us or by our affiliates.
      </p>

      <h2>DATA RETENTION</h2>
      <p>
        We will process your Data for as long as it remains necessary for the
        identified purpose or as required by law, which may extend beyond the
        termination of our relationship with you. We may process certain Data as
        necessary to prevent fraud or future abuse, or for legitimate business
        purposes, such as analysis of Anonymized Data, account recovery, or if
        required by law. All retained information will remain subject to the
        terms of this Privacy Policy.
      </p>

      <h2>AMENDMENT OF THIS PRIVACY POLICY</h2>
      <p>
        We reserve the right to change this Privacy Policy at any time. If we
        decide to change this Privacy Policy in the future, we will post or
        provide appropriate notice. Unless stated otherwise, our current Privacy
        Policy applies to all Data that we have about you and your account. The
        date on which the latest update was made is indicated at the top of this
        document. We recommend that you print a copy of this Privacy Policy for
        your reference and revisit this policy from time to time to ensure you
        are aware of any changes. Your continued use of the Services signifies
        your acceptance of any changes.
      </p>

      <h2>CONTACT US</h2>
      <p>
        You can help by keeping us informed of any changes such as a change of
        your personal contact information. If you would like to access your
        information, if you have any questions, comments, or suggestions or if
        you find any errors in our information about you, please contact us at
        privacy@deliciousai.com. If you have a complaint concerning our
        compliance with applicable privacy laws, we will investigate your
        complaint and if it is justified, we will take appropriate measures.
      </p>
    </Layout>
  )
}

export default PrivacyPolicy
